import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import SignaturePad from 'signature_pad';
import { ButtonModule } from 'primeng/button';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-signature-pad',
  templateUrl: './signature-pad.component.html',
  styleUrls: ['./signature-pad.component.scss'],
  imports: [ButtonModule, TranslocoPipe],
  standalone: true
})
export class SignaturePadComponent implements AfterViewInit {
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  @Input() value: string | null = null;
  @Input() readonly = false;

  @Output() signatureSaved = new EventEmitter<string>();
  @Output() signatureCleared = new EventEmitter<void>();

  signaturePad!: SignaturePad;

  isSaved = false;
  isEmpty = true;
  isReady = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    if (this.readonly) return;

    const canvas = this.canvasRef.nativeElement;

    this.resizeCanvas();
    this.signaturePad = new SignaturePad(canvas);

    this.isReady = true;
    this.updateEmptyState();
    this.cdr.markForCheck();

    const sync = () => {
      this.isSaved = false;
      this.updateEmptyState();
      this.cdr.markForCheck();
    };

    canvas.addEventListener('pointerup', sync);
    canvas.addEventListener('pointercancel', sync);
    canvas.addEventListener('mouseup', sync);
    canvas.addEventListener('touchend', sync, { passive: true });
  }

  clear(): void {
    if (this.readonly || !this.isReady) return;

    this.signaturePad.clear();
    this.isSaved = false;
    this.updateEmptyState();
    this.cdr.markForCheck();
    this.signatureCleared.emit();
  }

  save(): void {
    if (this.readonly || !this.isReady || this.isEmpty) return;

    const dataUrl = this.signaturePad.toDataURL();
    this.signatureSaved.emit(dataUrl);
    this.isSaved = true;
    this.cdr.markForCheck();
  }

  private updateEmptyState(): void {
    this.isEmpty = !this.signaturePad || this.signaturePad.isEmpty();
  }

  private resizeCanvas(): void {
    const canvas = this.canvasRef.nativeElement;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);
  }
}
